import { z } from 'zod';

import type { ToolExecutionContext } from '../types';

import { VideoStateTool } from './VideoStateTool';

import { getCaptionsSceneDesignsForTemplate } from 'js/src/data/templates/templates-selectors';
import type { RootState } from 'js/src/libs/RootState';
import {
    isVoiceDrivenSceneGroup,
    setCaptionsSceneDesignOperation,
    setSceneGroupCaptionsVisibilityOperation,
} from 'js/src/luminary';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';

export const setCaptionsSchema = z.object({
    enabled: z.boolean().optional().describe('Turn captions on or off for the entire video'),
    design_id: z
        .string()
        .optional()
        .describe('The caption design to apply globally. Omit to list available designs.'),
});
export type SetCaptionsArgs = z.infer<typeof setCaptionsSchema>;

export class SetCaptionsTool extends VideoStateTool<typeof setCaptionsSchema> {
    public static readonly toolName = 'set_captions';

    protected override readonly schema = setCaptionsSchema;
    protected override readonly toolName = SetCaptionsTool.toolName;
    protected override readonly toolDescription =
        "Control captions for a voiceover video. This turns captions on or off for the entire video (not per-scene) and sets the global caption design that applies to every voicedriven scene group. Captions only apply to voicedriven (voiceover or talking-head) videos. New videos inherit the user's last-used captions setting (usually off) — leave that alone unless the user asks for a change. Call with no arguments to list the available caption designs and the current caption state.";

    public override getToolDisplayName(): string {
        return 'Setting captions';
    }

    public get statusIcon(): string {
        return 'closed_caption';
    }

    public async execute(args: SetCaptionsArgs, _context: ToolExecutionContext) {
        const { enabled, design_id } = args;

        const voicedrivenSceneGroups = this.luminary.sceneGroups.filter(isVoiceDrivenSceneGroup);

        if (voicedrivenSceneGroups.length === 0) {
            return 'Error: This video has no voiceover or talking-head scene groups, so captions cannot be configured. Captions only apply to voicedriven videos.';
        }

        const templateId = this.luminary.template?.id;
        let designs: ReturnType<typeof getCaptionsSceneDesignsForTemplate> = [];
        if (templateId && this.getState) {
            try {
                designs = getCaptionsSceneDesignsForTemplate(this.getState(), templateId);
            } catch {
                // Template scene designs may be unavailable in the current state;
                // fall back to an empty list so caption visibility can still be toggled.
                designs = [];
            }
        }
        const designIds = designs.map((design) => design.id);

        const { setSceneGroupCaptionsVisibility, setCaptionsSceneDesign } = makeOperationsCallable(
            { setSceneGroupCaptionsVisibilityOperation, setCaptionsSceneDesignOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        if (design_id === undefined && enabled === undefined) {
            const currentlyVisible = voicedrivenSceneGroups.some((sg) => sg.captions.visible);
            const currentDesignId = voicedrivenSceneGroups[0]?.captions.sceneDesignId || 'none';
            const designLines = designs.length
                ? designs
                      .map((design) => `- ${design.id}: ${design.display_name ?? '(unnamed)'}`)
                      .join('\n')
                : '(none available)';
            return [
                `Captions are currently ${currentlyVisible ? 'on' : 'off'}.`,
                `Current caption design: ${currentDesignId}.`,
                'Available caption designs:',
                designLines,
            ].join('\n');
        }

        if (design_id !== undefined) {
            if (!designIds.includes(design_id)) {
                const available = designIds.length
                    ? designs
                          .map((design) => `${design.id} (${design.display_name ?? 'unnamed'})`)
                          .join(', ')
                    : 'none available';
                return `Error: Unknown caption design "${design_id}". Available designs: ${available}`;
            }
            setCaptionsSceneDesign(design_id);
        }

        if (enabled !== undefined) {
            for (const sceneGroup of voicedrivenSceneGroups) {
                setSceneGroupCaptionsVisibility(sceneGroup.id, enabled);
            }
        }

        const summaryParts: string[] = [];
        if (enabled !== undefined) {
            summaryParts.push(`Captions turned ${enabled ? 'on' : 'off'}.`);
        }
        if (design_id !== undefined) {
            summaryParts.push(`Applied caption design "${design_id}".`);
        }
        return `Success: ${summaryParts.join(' ')}`;
    }
}
